import { EXCLUDED_JOB_CONTRACT_NAMES } from '@/constants/job';
import {
	HIDDEN_HIRING_REASON_CODES,
	HIRING_REASON_PRIORITY_ORDER,
} from '@/constants/referential';
import { capitalize } from '@/helpers/string';

import type { LabelValue } from '@/types/common';
import type { IDepartment, IRegion } from '@/types/geo';
import type {
	DegreeLevelOption,
	IBusinessSectorDB,
	IDegreeLevelDB,
	IJobContract,
	IJobDB,
	JobOption,
} from '@/types/job';
import type {
	HiringReasonApi,
	HiringReasonOption,
	IInactivatedReason,
	IPaRefuseReason,
	IRejectedCompanyNeedReason,
	IUnavailabilityReasonDB,
} from '@/types/referential';
import type {
	ApplicantExperienceApi,
	IJobDuration,
	IJobFunction,
	IJobIndustry,
	IJobType,
} from '@/types/talentplug';

const excludedJobContractNames = new Set<string>(EXCLUDED_JOB_CONTRACT_NAMES);
const hiddenHiringReasonCodes = new Set<string>(HIDDEN_HIRING_REASON_CODES);
const hiringReasonPriorityOrder: string[] = [...HIRING_REASON_PRIORITY_ORDER];

export const sortJobDurations = (a: IJobDuration, b: IJobDuration): number => {
	const [aNumber] = a.name.split(' ');
	const [bNumber] = b.name.split(' ');
	return Number.parseInt(aNumber, 10) - Number.parseInt(bNumber, 10);
};

export const sortApplicantExperiences = (
	a: ApplicantExperienceApi,
	b: ApplicantExperienceApi
): number => a.talentplugId - b.talentplugId;

export const isSelectableJobContract = (contract: IJobContract): boolean =>
	!excludedJobContractNames.has(contract.name.toLowerCase());

export const mapJobsToOptions = (jobs: IJobDB[]): JobOption[] =>
	jobs
		.filter((job) => !job.notSelectable)
		.map((job) => ({
			label: job.name,
			value: job.id,
			slug: job.slugs[0],
		}));

export const mapDegreeLevelsToOptions = (
	degreeLevels: IDegreeLevelDB[]
): DegreeLevelOption[] =>
	degreeLevels.map((degree) => ({
		name: degree.name,
		value: degree.id,
		slug: degree.slug,
	}));

export const mapDegreeLevelOptionsToLabelValues = (
	degreeLevels: DegreeLevelOption[]
): LabelValue[] =>
	degreeLevels.map((degree) => ({
		label: degree.name,
		value: degree.value,
	}));

export const mapHiringReasonsToOptions = (
	hiringReasons: HiringReasonApi[]
): HiringReasonOption[] =>
	hiringReasons
		.filter(
			(reason) => !reason.code || !hiddenHiringReasonCodes.has(reason.code)
		)
		.map((reason) => ({
			id: reason.id,
			code: reason.code,
			label: reason.label ?? '',
			value: reason.id,
		}))
		.sort((a, b) => {
			const idxA = hiringReasonPriorityOrder.indexOf(a.code ?? '');
			const idxB = hiringReasonPriorityOrder.indexOf(b.code ?? '');
			if (idxA !== -1 && idxB !== -1) return idxA - idxB;
			if (idxA !== -1) return -1;
			if (idxB !== -1) return 1;
			return a.label.localeCompare(b.label);
		});

export const groupAndSortJobDurations = (
	jobDurations: IJobDuration[]
): LabelValue[] => {
	const grouped = [...jobDurations].sort(sortJobDurations).reduce(
		(acc, duration) => {
			const [, unit = ''] = duration.name.split(' ');
			if (unit.includes('jour')) {
				acc.days.push(duration);
			} else if (unit.includes('semaine')) {
				acc.weeks.push(duration);
			} else if (unit.includes('mois')) {
				acc.months.push(duration);
			}
			return acc;
		},
		{ days: [], weeks: [], months: [] } as Record<string, IJobDuration[]>
	);

	return [...grouped.days, ...grouped.weeks, ...grouped.months].map(
		(duration) => ({
			label: duration.name,
			value: duration.id,
		})
	);
};

export const mapApplicantExperiencesToOptions = (
	experiences: ApplicantExperienceApi[]
): LabelValue[] =>
	[...experiences].sort(sortApplicantExperiences).map((experience) => ({
		label: experience.name,
		value: experience.id,
	}));

export const mapJobContractsToOptions = (
	contracts: IJobContract[]
): LabelValue[] =>
	contracts.filter(isSelectableJobContract).map((contract) => ({
		label: contract.name,
		value: contract.id,
	}));

export const mapBusinessSectorsToOptions = (
	businessSectors: IBusinessSectorDB[]
): LabelValue[] =>
	businessSectors.map((businessSector) => ({
		label: capitalize(businessSector.name) || businessSector.name,
		value: businessSector.id,
	}));

export const mapUnavailabilityReasonsToOptions = (
	reasons: IUnavailabilityReasonDB[]
): LabelValue[] =>
	reasons.map((reason) => ({
		label: reason.label,
		value: reason.code,
	}));

export const mapPaRefuseReasonsToOptions = (
	reasons: IPaRefuseReason[]
): LabelValue[] =>
	reasons.map((reason) => ({
		label: reason.reason,
		value: reason.id,
	}));

export const mapDepartmentsToOptions = (departments: IDepartment[]) =>
	departments.map((department) => ({
		label: department.name,
		value: department.code,
		regionCode: department.regionCode,
	}));

export const mapRegionsToOptions = (regions: IRegion[]): LabelValue[] =>
	regions.map((region) => ({
		label: region.name,
		value: region.code,
	}));

export const mapJobIndustriesToOptions = (industries: IJobIndustry[]) =>
	industries.map((industry) => ({
		label: industry.name,
		value: industry.id,
		talentplugId: industry.talentplugId,
	}));

export const mapJobFunctionsToOptions = (jobFunctions: IJobFunction[]) =>
	jobFunctions.map((jobFunction) => ({
		label: jobFunction.name,
		value: jobFunction.id,
		talentplugId: jobFunction.talentplugId,
	}));

export const mapJobTypesToOptions = (jobTypes: IJobType[]) =>
	jobTypes.map((jobType) => ({
		label: jobType.name,
		value: jobType.id,
		talentplugId: jobType.talentplugId,
	}));

export const mapRejectedCompanyNeedReasonsToMap = (
	reasons: IRejectedCompanyNeedReason[]
): Map<string, string> =>
	new Map(reasons.map((reason) => [reason.id, reason.label]));

export const mapInactivatedReasonsToMap = (
	reasons: IInactivatedReason[]
): Map<string, string> =>
	new Map(reasons.map((reason) => [reason.id, reason.reason]));
